import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { InvoiceMatchPaymentsDataService } from './invoice-match-payments-data.service';
import { concat, Observable, of } from 'rxjs';
import {
  ActionsEnum,
  FetchUnmatchedPaymentsError,
  fetchUnmatchedPaymentsError,
  FetchUnmatchedPaymentsSuccess,
  fetchUnmatchedPaymentsSuccess,
  MatchPayment,
  matchPaymentError,
  MatchPaymentError,
  matchPaymentSuccess,
  MatchPaymentSuccess,
} from './invoice-match-payments-data.actions';
import {
  SaveEntityStore,
  saveEntityStore,
} from '../entity-data/entity-data.actions';
import { catchError, flatMap, map, switchMap } from 'rxjs/operators';
import { UnmatchedPaymentsWithClients } from './models/unmatched-payments-with-clients.model';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../dialog/dialog.service';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { NavigationService } from '../navigation/navigation.service';
import { LocalizationService } from '../localization/localization.service';
import { MoneyPipe } from '../shared/pipes/money/money.pipe';
import { Document } from '../document-data/document.model';
import { UnmatchedPayment } from './models/unmatched-payment.model';
import { IToasterConfig, IToasterData } from '../toaster/toaster.data';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';
import { ToasterDispatcher } from '../toaster/toaster.dispatcher';
import { INVOICES_MASTER_ROUTE } from '../main/main.data';
import { INVOICES_UNMATCHED_PAYMENTS_ROUTE } from '../invoices/invoices.data';
import { InvoiceDataDispatcher } from '../invoice-data/invoice-data.dispatcher';
import { EntityTypesEnum } from '../../app.enum';
import { ShowToast, showToast } from '../toaster/toaster.actions';

@Injectable()
export class InvoiceMatchPaymentsDataEffects {
  constructor(
    private _actions: Actions,
    private _invoiceMatchPaymentsDataService: InvoiceMatchPaymentsDataService,
    private _translateService: TranslateService,
    private _localizationService: LocalizationService,
    private _dialogService: DialogService,
    private _globalBannerService: GlobalBannerService,
    private _navigationService: NavigationService,
    private _moneyPipe: MoneyPipe,
    private _toasterDispatcher: ToasterDispatcher,
    private _invoiceDataDispatcher: InvoiceDataDispatcher
  ) {}

  @Effect()
  fetchUnmatchedPayments: Observable<
    FetchUnmatchedPaymentsSuccess | FetchUnmatchedPaymentsError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_UNMATCHED_PAYMENTS),
    switchMap(() => {
      return this._invoiceMatchPaymentsDataService
        .fetchUnmatchedPayments()
        .pipe(
          map((unmatchedPayments: UnmatchedPaymentsWithClients) =>
            fetchUnmatchedPaymentsSuccess(unmatchedPayments)
          ),
          catchError((error) => of(fetchUnmatchedPaymentsError(error)))
        );
    })
  );

  @Effect()
  matchPayment: Observable<
    MatchPaymentSuccess | MatchPaymentError
  > = this._actions.pipe(
    ofType(ActionsEnum.MATCH_PAYMENT),
    switchMap(({ payload }: MatchPayment) => {
      const { document, payment, amountToMatch } = payload;

      const matchPaymentInProgressLabel = this._buildMatchPaymentInProgressLabel(
        document,
        payment,
        amountToMatch
      );
      this._dialogService.openLoadingDialog(matchPaymentInProgressLabel);

      return this._invoiceMatchPaymentsDataService
        .matchPayment(document, payment, amountToMatch)
        .pipe(
          map((unmatchedPayments: UnmatchedPaymentsWithClients) =>
            matchPaymentSuccess(document, unmatchedPayments)
          ),
          catchError((error) => of(matchPaymentError(error)))
        );
    })
  );

  @Effect()
  matchPaymentSuccess: Observable<
    FetchUnmatchedPaymentsSuccess | SaveEntityStore | ShowToast
  > = this._actions.pipe(
    ofType(ActionsEnum.MATCH_PAYMENT_SUCCESS),
    switchMap(({ payload }: MatchPaymentSuccess) => {
      // Get updated invoice from server so we can update the store
      // Useful to move fully paid invoice from unpaid to paid list
      return this._invoiceDataDispatcher
        .getEntityById(payload.document.id)
        .pipe(
          flatMap((document: Document) => {
            this._dialogService.closeLoadingDialog();

            const toastData: IToasterData = {
              label: this._translateService.instant(
                'DOCUMENT.INVOICE.MATCH_PAYMENTS.MATCH_PAYMENT_SUCCESS'
              ),
              containsHtml: true,
            };
            const toastConfig: IToasterConfig = {
              type: 'match-payment-success',
              component: MessageToastComponent,
              data: toastData,
            };

            this._navigationService.goTo(
              [INVOICES_MASTER_ROUTE, INVOICES_UNMATCHED_PAYMENTS_ROUTE].join(
                '/'
              )
            );

            return concat([
              fetchUnmatchedPaymentsSuccess(payload.unmatchedPayments),
              saveEntityStore(EntityTypesEnum.invoice, document, false),
              showToast(toastConfig),
            ]);
          })
        );
    })
  );

  @Effect({
    dispatch: false,
  })
  matchPaymentError = this._actions.pipe(
    ofType(ActionsEnum.MATCH_PAYMENT_ERROR),
    switchMap(({ payload }: MatchPaymentError) => {
      this._dialogService.closeLoadingDialog();

      this._navigationService.goTo(
        [INVOICES_MASTER_ROUTE, INVOICES_UNMATCHED_PAYMENTS_ROUTE].join('/')
      );

      const error =
        payload.error instanceof Error
          ? payload.error
          : new Error('Match failed — please try again');
      const label = this._translateService.instant(
        'DOCUMENT.INVOICE.MATCH_PAYMENTS.MATCH_PAYMENT_FAILED'
      );

      return this._globalBannerService.showSystemErrorBanner(error, label);
    })
  );

  private _buildMatchPaymentInProgressLabel(
    document: Document,
    payment: UnmatchedPayment,
    amountToMatch: number
  ) {
    const outstandingBalance = document.calculationResult.outstandingBalance;
    const { unmatchedAmount, currencyCode } = payment;
    const locale = this._localizationService.getLocale();

    const hasUnmatchedAmountLeft = unmatchedAmount > amountToMatch;
    const hasOutstandingBalanceLeft = amountToMatch < outstandingBalance;

    if (hasUnmatchedAmountLeft) {
      const unmatchedAmountLeft = unmatchedAmount - amountToMatch;

      return this._translateService.instant(
        'DOCUMENT.INVOICE.MATCH_PAYMENTS.UNMATCHED_AMOUNT_LEFT',
        {
          invoiceNumber: document.docNumber,
          unmatchedPaymentAmount: this._moneyPipe.transform(
            unmatchedAmountLeft,
            currencyCode,
            locale
          ),
        }
      );
    }

    if (hasOutstandingBalanceLeft) {
      const outstandingBalanceLeft = outstandingBalance - amountToMatch;

      return this._translateService.instant(
        'DOCUMENT.INVOICE.MATCH_PAYMENTS.OUTSTANDING_BALANCE_LEFT',
        {
          invoiceNumber: document.docNumber,
          outstandingBalance: this._moneyPipe.transform(
            outstandingBalanceLeft,
            currencyCode,
            locale
          ),
        }
      );
    }

    return this._translateService.instant(
      'DOCUMENT.INVOICE.MATCH_PAYMENTS.FULL_PAYMENT',
      {
        invoiceNumber: document.docNumber,
      }
    );
  }
}
